import React, { useContext } from 'react';
import {
  type ConnectionLineComponentProps,
  getBezierPath,
  getSmoothStepPath,
} from '@xyflow/react';
import { EditorContext } from '../EditorContext';
import { GhostPath } from './ConnectionLine.styled';
import { buildOrthoPath, isForwardOrthoLayout } from './buildOrthoPath';

// Fallback corner radius for `getSmoothStepPath` — used when the
// forward-ortho layout doesn't fit (backward drags, ports too close).
// Mirrors `BranchedEdge`'s fallback so the drag ghost and the
// committed wire share the same curvature family.
const ORTHO_FALLBACK_BORDER_RADIUS = 36;

/**
 * Custom {@code connectionLineComponent} for the process canvas. Renders
 * the design's {@code .ns-wire--pending} ghost: a thinner 5-4 dashed
 * stroke with an infinite {@code stroke-dashoffset} marquee at 1.2s
 * linear, switching to red while the live connection target is invalid.
 *
 * Reads {@code wireStyle} via {@code useContext} (not the throwing
 * helper) so unit tests that mount the connection line without an
 * editor provider still render — they fall back to bezier.
 *
 * Ortho geometry matches {@link BranchedEdge} exactly: forward L→R
 * layouts use the design's bespoke `buildOrthoPath` (short source stub,
 * 24px arcs, long target approach) so the drop preview reads as a
 * continuation of the committed wire, not a separate generator. Backward
 * / too-close layouts fall back to smoothstep.
 */
const ConnectionLine: React.FC<ConnectionLineComponentProps> = ({
  fromX,
  fromY,
  toX,
  toY,
  fromPosition,
  toPosition,
  connectionStatus,
}) => {
  const editor = useContext(EditorContext);
  const useOrtho = editor?.wireStyle === 'ortho';

  let path: string;
  if (useOrtho && isForwardOrthoLayout(fromX, toX)) {
    path = buildOrthoPath(fromX, fromY, toX, toY);
  } else if (useOrtho) {
    [path] = getSmoothStepPath({
      sourceX: fromX,
      sourceY: fromY,
      sourcePosition: fromPosition,
      targetX: toX,
      targetY: toY,
      targetPosition: toPosition,
      borderRadius: ORTHO_FALLBACK_BORDER_RADIUS,
    });
  } else {
    [path] = getBezierPath({
      sourceX: fromX,
      sourceY: fromY,
      sourcePosition: fromPosition,
      targetX: toX,
      targetY: toY,
      targetPosition: toPosition,
    });
  }

  return (
    <g data-testid="process-connection-line">
      <GhostPath d={path} $invalid={connectionStatus === 'invalid'} />
    </g>
  );
};

export default ConnectionLine;
